import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import ApplicationLayoutBodyBanner from '@rio-cloud/rio-uikit/ApplicationLayoutBodyBanner';
import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import LoadMoreButton from '@rio-cloud/rio-uikit/LoadMoreButton';
import SortDirection, { type SortDirectionType } from '@rio-cloud/rio-uikit/SortDirection';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import useLocalStorage from '@rio-cloud/rio-uikit/useLocalStorage';
import intersection from 'lodash/intersection';
import { type Identifier, orderBy } from 'natural-orderby';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { config } from '../../../../config';
import { getUserAccount } from '../../../../configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import { reportErrorToSentry } from '../../../../configuration/setup/sentry';
import AssetsListDetailContainer from '../../containers/assets/details/AssetsListDetail.container';
import {
    assetTableSortChanges,
    getAssetTableSortColumn,
    getAssetTableSortDir,
    getAssetsTableViewType,
    getSearchString,
    getShowActiveAssets,
    getShowAssetsWithDataSource,
    getShowAssetsWithoutDataSource,
    getShowArchivedAssets,
    getTableCompactRows,
    getTableGridLines,
    openEasyOnboardingDialog,
    setSelectedAssetId,
} from '../../reducers/assets/assetsSlice';
import { useGetDeviceSharingsQuery } from '../../../services/assetsApi';
import {
    useAddAssetTagMutation,
    useCreateTagMutation,
    useGetAssetsQuery,
    useGetAssociationsQuery,
    useGetTagsQuery,
    useRemoveAssetTagMutation,
} from '../../../services/assetAdministrationApi';
import { getAssetAssociatedDeviceMap } from '../../reducers/assets/assetsSliceUtils';
import { ReleaseInfoBannerContent } from '../ReleaseInfoBannerContent';
import { AssetsList } from './AssetsList';
import { AssetsListToolbar } from './AssetsListToolbar';
import { BulkArchiveAssetConfirmationDialog } from './BulkArchiveAssetConfirmationDialog';
import { formatLicensePlateForDisplay } from './CsvExport.helper';
import { deviceTypeToDisplayedString } from './TelematicsName';
import {
    closableSuccessNotification,
    nonVanishingErrorNotification,
} from './ClickableNotifications';
import {
    ASSETS_PER_PAGE,
    getAssetTableColumnsStorageKey,
    isAssetTableColumnSortable,
    normalizeAssetTableColumns,
} from './assetsUtils';
import { TagManagerWrapper } from './details/TagManagerWrapper';
import { type DeviceType, type SelectedTag, SelectedTagStatus } from './details/types';
import {
    type Asset,
    AssetStatus,
    type AssetType,
    type AssociatedDevice,
    type Tag,
    type TableColumnDetails,
} from './types';

const IS_FEATURE_BANNER_ENABLED = false;

const sleep = (milliseconds: number) => new Promise<void>(resolve => window.setTimeout(resolve, milliseconds));

export const Assets = () => {
    // Redux hooks
    const dispatch = useAppDispatch();

    const selectedAssetId = useAppSelector(state => state.assets.selectedAssetId);
    const tableViewType = useAppSelector(getAssetsTableViewType);
    const accountId = useAppSelector(getUserAccount) ?? null;
    const searchString = useAppSelector(getSearchString);
    const showAssetsWithDataSource = useAppSelector(getShowAssetsWithDataSource);
    const showAssetsWithoutDataSource = useAppSelector(getShowAssetsWithoutDataSource);
    const showActiveAssets = useAppSelector(getShowActiveAssets);
    const showArchivedAssets = useAppSelector(getShowArchivedAssets);
    const gridLines = useAppSelector(getTableGridLines);
    const compactRows = useAppSelector(getTableCompactRows);
    const sortBy = useAppSelector(getAssetTableSortColumn);
    const sortDir = useAppSelector(getAssetTableSortDir);
    const { data: assets = [], isLoading: assetsLoading } = useGetAssetsQuery();
    const { data: associations = [] } = useGetAssociationsQuery();
    const { data: availableTags = [] } = useGetTagsQuery(accountId);
    const [createTag] = useCreateTagMutation();
    const [addAssetTag] = useAddAssetTagMutation();
    const [removeAssetTag] = useRemoveAssetTagMutation();
    const devices = getAssetAssociatedDeviceMap(associations);

    const intl = useIntl();

    // State hooks
    const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
    const [unsavedAssetChanges, setUnsavedAssetChanges] = useState(false);
    const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
    const [tagManagerSelectedTags, setTagManagerSelectedTags] = useState<SelectedTag[]>([]);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showAddToGroupDialog, setShowAddToGroupDialog] = useState(false);
    const [showBulkArchiveDialog, setShowBulkArchiveDialog] = useState(false);
    const [showRemoveFromGroupDialog, setShowRemoveFromGroupDialog] = useState(false);
    const [numberOfAssetsToDisplay, setNumberOfAssetsToDisplay] = useState(ASSETS_PER_PAGE);
    const [nextRowId, setNextRowId] = useState<string | null>(null);
    const [tableResetKey, setTableResetKey] = useState(0);
    const tableColumnsStorageKey = getAssetTableColumnsStorageKey(accountId);
    const [storedTableColumns, setStoredTableColumns, removeStoredTableColumns] = useLocalStorage<TableColumnDetails[]>(
        tableColumnsStorageKey,
        []
    );
    const previousTableColumnsStorageKey = useRef(tableColumnsStorageKey);
    const hasTableColumnsStorageKeyChanged = previousTableColumnsStorageKey.current !== tableColumnsStorageKey;
    const tableColumns = normalizeAssetTableColumns(hasTableColumnsStorageKeyChanged ? undefined : storedTableColumns);
    const showSharingsColumn = tableColumns.some(column => column.key === 'sharings' && column.visible);
    const { data: deviceSharings } = useGetDeviceSharingsQuery(undefined, { skip: !showSharingsColumn });

    // Other hooks (useMemo, useCallback, useEffect)
    useEffect(() => {
        const nextSelectedAsset = assets.find(asset => asset.id === selectedAssetId) || null;
        setSelectedAsset(nextSelectedAsset);
    }, [setSelectedAsset, selectedAssetId, assets]);

    useEffect(() => {
        if (!hasTableColumnsStorageKeyChanged) {
            return;
        }

        const storedValue = window.localStorage.getItem(tableColumnsStorageKey);

        previousTableColumnsStorageKey.current = tableColumnsStorageKey;
        setStoredTableColumns(storedValue ? JSON.parse(storedValue) : []);
    }, [hasTableColumnsStorageKeyChanged, setStoredTableColumns, tableColumnsStorageKey]);

    useEffect(() => {
        const normalizedStoredColumns = normalizeAssetTableColumns(storedTableColumns);

        if (
            !hasTableColumnsStorageKeyChanged &&
            JSON.stringify(storedTableColumns) !== JSON.stringify(normalizedStoredColumns)
        ) {
            setStoredTableColumns(normalizedStoredColumns);
        }
    }, [hasTableColumnsStorageKeyChanged, storedTableColumns, setStoredTableColumns]);

    const handleActiveRowChange = (rowId: string | undefined) => {
        if (unsavedAssetChanges && rowId !== undefined) {
            if (rowId !== selectedAssetId) {
                setNextRowId(rowId);
            }
            setShowConfirmDialog(true);
        } else if (rowId && rowId !== selectedAssetId) {
            dispatch(setSelectedAssetId(rowId));
        } else {
            dispatch(setSelectedAssetId(null));
        }
    };

    const dispatchHandleSortChange = (column: string, sortDir: SortDirectionType) => {
        if (isAssetTableColumnSortable(column, tableColumns)) {
            dispatch(assetTableSortChanges({ sortBy: column, sortDir: sortDir }));
        }
    };

    const handleSortChange = (column: string) => {
        if (column === sortBy) {
            const newSortDir = sortDir === SortDirection.ASCENDING ? SortDirection.DESCENDING : SortDirection.ASCENDING;
            dispatchHandleSortChange(column, newSortDir);
        } else {
            dispatchHandleSortChange(column, SortDirection.ASCENDING);
        }
    };

    const handleToggleAll = (shouldSelect: boolean) => {
        const allDisplayedRowIds = getFilteredAssets().map(asset => asset.id);

        if (shouldSelect) {
            setSelectedAssetIds(allDisplayedRowIds);
        } else {
            setSelectedAssetIds([]);
        }
    };

    const handleLoadMore = () => {
        setNumberOfAssetsToDisplay(numberOfAssetsToDisplay + ASSETS_PER_PAGE);
    };

    const handleColumnOrderChange = (columnOrder: string[]) => {
        setStoredTableColumns(currentColumns => {
            const normalizedColumns = normalizeAssetTableColumns(currentColumns);
            const orderedColumns = columnOrder
                .map(columnKey => normalizedColumns.find(column => column.key === columnKey))
                .filter((column): column is TableColumnDetails => column !== undefined);

            const missingColumns = normalizedColumns.filter(
                column => !orderedColumns.some(orderedColumn => orderedColumn.key === column.key)
            );

            return [...orderedColumns, ...missingColumns];
        });
    };

    const handleColumnVisibilityChange = (columnKey: string, visible: boolean) => {
        setStoredTableColumns(currentColumns =>
            normalizeAssetTableColumns(currentColumns).map(column =>
                column.key === columnKey ? { ...column, visible } : column
            )
        );
    };

    const handleColumnResize = (columnKey: string, width?: number | string) => {
        setStoredTableColumns(currentColumns =>
            normalizeAssetTableColumns(currentColumns).map(column => {
                if (column.key !== columnKey) {
                    return column;
                }

                if (width === undefined) {
                    const { width: _width, ...columnWithoutWidth } = column;

                    return columnWithoutWidth;
                }

                return { ...column, width };
            })
        );
    };

    const handleColumnsReset = () => {
        removeStoredTableColumns();
        setTableResetKey(currentValue => currentValue + 1);
    };

    const createNewTags = async (tagsToUpdate: SelectedTag[]) => {
        const newTags = tagsToUpdate.filter(currentTag => currentTag.status === SelectedTagStatus.created);
        await Promise.all(
            newTags.map(tag =>
                createTag({ id: tag.id, type: tag.type, name: tag.name, account_id: tag.account_id }).unwrap()
            )
        );
    };

    const addTagWithRetry = async (assetId: string, tagId: string) => {
        const maximumRetries = Math.max(config.actions.assets.maximumCreateTagRetries, 1);

        for (let retryCount = 0; retryCount < maximumRetries; retryCount++) {
            try {
                await addAssetTag({ assetId, tagId }).unwrap();
                return;
            } catch {
                if (retryCount === maximumRetries - 1) {
                    reportErrorToSentry(
                        new Error(`Could not add tag ${tagId} to asset ${assetId} after ${maximumRetries} retries`)
                    );
                    throw new Error(`Could not add tag ${tagId} to asset ${assetId}`);
                }

                await sleep(config.actions.assets.sleepBetweenCreateTagRetriesMs);
            }
        }
    };

    const updateAssetTag = async (asset: Asset, tagsToUpdate: SelectedTag[]) => {
        for (const tag of tagsToUpdate) {
            if (tag.status === SelectedTagStatus.removed) {
                await removeAssetTag({ assetId: asset.id, tagId: tag.id }).unwrap();
                closableSuccessNotification(
                    <FormattedMessage id={'intl-msg:asset.tag.remove.success'} values={{ tagName: tag.name }} />,
                    'success-notification-tag-removed'
                );
            }
            if (tag.status === SelectedTagStatus.added || tag.status === SelectedTagStatus.created) {
                await addTagWithRetry(asset.id, tag.id);
                closableSuccessNotification(
                    <FormattedMessage id={'intl-msg:asset.tag.add.success'} values={{ tagName: tag.name }} />,
                    'success-notification-tag-added'
                );
            }
        }
    };

    const assetNeedsTagUpdate = (selectedTags: SelectedTag[], asset: Asset) => {
        return selectedTags.some(assetTag => {
            if (assetTag.status === SelectedTagStatus.added) {
                return !asset.tags.includes(assetTag.id);
            } else if (assetTag.status === SelectedTagStatus.removed) {
                return asset.tags.includes(assetTag.id);
            } else {
                return true;
            }
        });
    };

    const createAndUpdateAssetTagsFromSelection = async () => {
        await createNewTags(tagManagerSelectedTags);
        const selectedTags = tagManagerSelectedTags;
        const selectedAssets = assets.filter(
            asset => selectedAssetIds.includes(asset.id) && assetNeedsTagUpdate(selectedTags, asset)
        );
        await Promise.all(selectedAssets.map(asset => updateAssetTag(asset, tagManagerSelectedTags)));
    };

    const onClickAddToGroupDialogConfirm = () => {
        setShowAddToGroupDialog(false);
        void createAndUpdateAssetTagsFromSelection().catch(() =>
            nonVanishingErrorNotification(<FormattedMessage id={'intl-msg:create.error'} />, 'T02')
        );
    };

    const onClickRemoveFromGroupDialogConfirm = () => {
        setShowRemoveFromGroupDialog(false);
        void createAndUpdateAssetTagsFromSelection().catch(() =>
            nonVanishingErrorNotification(<FormattedMessage id={'intl-msg:create.error'} />, 'T01')
        );
    };

    const onSidebarChange = () => {
        setUnsavedAssetChanges(false);
        setShowConfirmDialog(false);
        setNextRowId(null);
        dispatch(setSelectedAssetId(nextRowId));
    };

    const getAddToGroupDialogAvailableTags = () => {
        const selectedTagsIntersection = intersection(
            ...assets.filter(asset => selectedAssetIds.includes(asset.id)).map(asset => asset.tags)
        );
        return availableTags.filter(tag => !selectedTagsIntersection.includes(tag.id));
    };

    const getRemoveFromGroupDialogAssetTags = () =>
        assets.filter(asset => selectedAssetIds.includes(asset.id)).flatMap(asset => asset.tags);

    const getRemoveFromGroupDialogAvailableTags = () =>
        availableTags.filter(tag => getRemoveFromGroupDialogAssetTags().includes(tag.id));

    const onTagListChange = (selectedTags: SelectedTag[]) => {
        setTagManagerSelectedTags(selectedTags.filter(tag => tag.status !== SelectedTagStatus.unchanged));
    };

    const getDevicesForAsset = (assetId: string): AssociatedDevice[] => {
        const devicesForAsset = devices[assetId];
        return devicesForAsset === undefined ? [] : devicesForAsset;
    };

    const getFormattedMessageAssetType = (assetType: AssetType) =>
        intl.formatMessage({ id: `intl-msg:asset.type.${assetType.toString()}` }).toLowerCase();

    const getFormattedMessageDeviceType = (deviceType: DeviceType) => deviceTypeToDisplayedString(deviceType);
    const visibleColumnKeys = tableColumns.filter(column => column.visible).map(column => column.key);

    const getSearchableValuesForColumn = (asset: Asset, columnKey: string): string[] => {
        switch (columnKey) {
            case 'vehicle':
                return [
                    asset.name,
                    asset.identification?.trim() ? asset.identification : '',
                    getFormattedMessageAssetType(asset.type),
                ];
            case 'name':
                return [asset.name];
            case 'type':
                return [getFormattedMessageAssetType(asset.type)];
            case 'identification':
                return asset.identification?.trim() ? [asset.identification] : [];
            case 'license_plate':
                return [formatLicensePlateForDisplay(asset)];
            case 'brand':
                return [asset.brand ?? ''];
            case 'fuel_type':
                return [
                    asset.masterdata?.fuel_type
                        ? intl.formatMessage({ id: `intl-msg:masterdata.fuel-type.${asset.masterdata.fuel_type}` })
                        : '',
                ];
            case 'status':
                return [intl.formatMessage({ id: `intl-msg:asset.status.${asset.status}` })];
            case 'tags':
                return availableTags.filter(tag => asset.tags.includes(tag.id)).map(tag => tag.name);
            case 'devices':
                return getDevicesForAsset(asset.id).map(associatedDevice =>
                    getFormattedMessageDeviceType(associatedDevice.device.type)
                );
            default:
                return [];
        }
    };

    const filterBySearchValue = (assets: Asset[]): Asset[] => {
        const searchTerms = searchString.toLocaleLowerCase().split(' ').filter(Boolean);

        if (searchTerms.length === 0) {
            return assets;
        }

        return searchTerms.reduce(
            (prevResult, searchTerm) =>
                prevResult.filter(asset =>
                    visibleColumnKeys.some(columnKey =>
                        getSearchableValuesForColumn(asset, columnKey).some(value =>
                            value.toLocaleLowerCase().includes(searchTerm)
                        )
                    )
                ),
            assets
        );
    };

    const filteredByFilterOptions = (assets: Asset[]): Asset[] => {
        return assets
            .filter(asset => {
                const hasDataSource = getDevicesForAsset(asset.id).length > 0;

                if (hasDataSource) {
                    return showAssetsWithDataSource;
                }

                return showAssetsWithoutDataSource;
            })
            .filter(asset => (!showActiveAssets ? asset.status !== AssetStatus.active : true))
            .filter(asset => (!showArchivedAssets ? asset.status !== AssetStatus.archived : true));
    };

    const getFilteredAssets = () => {
        const assetsFilteredBySearchString = filterBySearchValue(assets);
        return filteredByFilterOptions(assetsFilteredBySearchString);
    };

    const getSortedAssets = (assets: Asset[]) => {
        if (sortBy) {
            if (sortBy === 'fuel_type') {
                return orderBy<Asset>(
                    assets,
                    [
                        asset =>
                            asset.masterdata?.fuel_type
                                ? intl.formatMessage({
                                      id: `intl-msg:masterdata.fuel-type.${asset.masterdata.fuel_type}`,
                                  })
                                : '',
                    ],
                    [sortDir]
                );
            }

            if (sortBy === 'type') {
                return orderBy<Asset>(assets, [asset => getFormattedMessageAssetType(asset.type)], [sortDir]);
            }

            if (sortBy === 'sharings') {
                return orderBy<Asset>(
                    assets,
                    [
                        asset =>
                            new Set(
                                deviceSharings
                                    ?.filter(sharing => sharing.asset_id === asset.id)
                                    .map(sharing => sharing.shared_account_id)
                            ).size,
                    ],
                    [sortDir]
                );
            }

            const sortField = sortBy === 'vehicle' ? 'name' : sortBy;

            return orderBy<Asset>(assets, [sortField as Identifier<Asset>], [sortDir]);
        } else {
            return assets;
        }
    };

    const sortedAssets = getSortedAssets(getFilteredAssets());
    const assetsToRender = sortedAssets.slice(0, numberOfAssetsToDisplay);

    // Render helpers (optional)
    const renderTagManagerWrapper = (availableTags: Tag[], assetTags: string[] | undefined, useCustomTags: boolean) => (
        <TagManagerWrapper
            accountId={accountId}
            assetTags={assetTags}
            onTagListChange={onTagListChange}
            availableTags={availableTags}
            useCustomTags={useCustomTags}
            customMessage={
                useCustomTags ? 'intl-msg:tag-manager.create-add-message' : 'intl-msg:tag-manager.remove-message'
            }
        />
    );

    return (
        <>
            {showConfirmDialog && (
                <ConfirmationDialog
                    show={showConfirmDialog}
                    title={intl.formatMessage({ id: 'intl-msg:confirmation-dialog.title' })}
                    content={<FormattedMessage id={'intl-msg:confirmation-dialog.content'} />}
                    onClickConfirm={onSidebarChange}
                    onClickCancel={() => {
                        setNextRowId(null);
                        setShowConfirmDialog(false);
                    }}
                    cancelButtonText={<FormattedMessage id={'intl-msg:confirmation-dialog.button.cancel'} />}
                    confirmButtonText={<FormattedMessage id={'intl-msg:confirmation-dialog.button.confirm'} />}
                    disableConfirm={false}
                    useOverflow={false}
                    bsSize={'sm'}
                />
            )}
            {showAddToGroupDialog && (
                <ConfirmationDialog
                    show={showAddToGroupDialog}
                    title={intl.formatMessage({ id: 'intl-msg:addToGroup' })}
                    content={renderTagManagerWrapper(getAddToGroupDialogAvailableTags(), [], true)}
                    bsSize={'sm'}
                    onClickConfirm={onClickAddToGroupDialogConfirm}
                    onClickCancel={() => setShowAddToGroupDialog(false)}
                    cancelButtonText={<FormattedMessage id={'intl-msg:add-to-group-dialog.button.cancel'} />}
                    confirmButtonText={
                        <div data-cy={'confirmation-dialog-confirm-button'}>
                            <FormattedMessage id={'intl-msg:add-to-group-dialog.button.confirm'} />
                        </div>
                    }
                    useOverflow={false}
                />
            )}
            {showRemoveFromGroupDialog && (
                <ConfirmationDialog
                    show={showRemoveFromGroupDialog}
                    title={intl.formatMessage({ id: 'intl-msg:removeFromGroup' })}
                    content={renderTagManagerWrapper(
                        getRemoveFromGroupDialogAvailableTags(),
                        getRemoveFromGroupDialogAssetTags(),
                        false
                    )}
                    bsSize={'sm'}
                    onClickConfirm={onClickRemoveFromGroupDialogConfirm}
                    onClickCancel={() => setShowRemoveFromGroupDialog(false)}
                    cancelButtonText={<FormattedMessage id={'intl-msg:add-to-group-dialog.button.cancel'} />}
                    confirmButtonText={
                        <div data-cy={'confirmation-dialog-confirm-button'}>
                            <FormattedMessage id={'intl-msg:add-to-group-dialog.button.confirm'} />
                        </div>
                    }
                    useOverflow={false}
                />
            )}
            {showBulkArchiveDialog && (
                <BulkArchiveAssetConfirmationDialog
                    show={showBulkArchiveDialog}
                    assets={assets.filter(asset => selectedAssetIds.includes(asset.id))}
                    hideBulkArchiveAssetDialog={() => setShowBulkArchiveDialog(false)}
                />
            )}
            <ApplicationLayout.Sidebar className='right'>
                {selectedAsset && (
                    <AssetsListDetailContainer
                        asset={selectedAsset}
                        onClose={onSidebarChange}
                        toggleConfirmDialog={setShowConfirmDialog}
                        toggleUnsavedAssetChanges={setUnsavedAssetChanges}
                    />
                )}
            </ApplicationLayout.Sidebar>
            <ApplicationLayout.Body
                banner={
                    IS_FEATURE_BANNER_ENABLED && (
                        <ApplicationLayoutBodyBanner>
                            <ReleaseInfoBannerContent />
                        </ApplicationLayoutBodyBanner>
                    )
                }
            >
                {assetsLoading ? (
                    <Spinner
                        text={
                            <FormattedMessage
                                id={'intl-msg:spinner.loading.assets'}
                                defaultMessage={'Loading vehicles...'}
                            />
                        }
                    />
                ) : (
                    <>
                        <AssetsListToolbar
                            assets={sortedAssets}
                            columns={tableColumns}
                            showCreateButton={assetsToRender.length > 0}
                            handleColumnOrderChange={handleColumnOrderChange}
                            handleColumnVisibilityChange={handleColumnVisibilityChange}
                            handleColumnsReset={handleColumnsReset}
                        />
                        <AssetsList
                            assets={assetsToRender}
                            columns={tableColumns}
                            tableResetKey={tableResetKey}
                            devices={devices}
                            searchString={searchString}
                            onCreateAsset={() => dispatch(openEasyOnboardingDialog())}
                            selectedAsset={selectedAsset}
                            tableViewType={tableViewType}
                            handleActiveRowChange={handleActiveRowChange}
                            tags={availableTags}
                            handleSortChange={handleSortChange}
                            sortBy={sortBy}
                            sortDir={sortDir}
                            gridLines={gridLines}
                            compactRows={compactRows}
                            handleToggleAll={handleToggleAll}
                            handleColumnResize={handleColumnResize}
                            handleColumnOrderChange={handleColumnOrderChange}
                            handleColumnVisibilityChange={handleColumnVisibilityChange}
                            selectedAssetIds={selectedAssetIds}
                            handleSelectionChange={setSelectedAssetIds}
                            showAddToGroupDialog={setShowAddToGroupDialog}
                            showBulkArchiveDialog={setShowBulkArchiveDialog}
                            showRemoveFromGroupDialog={setShowRemoveFromGroupDialog}
                        />
                        {assetsToRender.length > 0 && (
                            <LoadMoreButton
                                data-test-id={'loadMoreButton'}
                                loaded={assetsToRender.length}
                                total={sortedAssets.length}
                                onLoadMore={handleLoadMore}
                                loadMoreMessage={<FormattedMessage id={'intl-msg:loadMore'} />}
                                noMoreMessage={<FormattedMessage id={'intl-msg:noMore'} />}
                            />
                        )}
                    </>
                )}
            </ApplicationLayout.Body>
        </>
    );
};
